// src/sections/CMS/components/customPage/FilteredProductsBlockRenderer.tsx  (CONSUMER)
// multiFilterFields comes from cms_category.multi_filter_fields (page level).
// The block's component_fields only stores item_group as a reference —
// all filter config (values, display flags, match types) lives on the category.

import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import FilterComponent from '../../../../components/shared/filter/FilterComponent';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FilteredProductsBlockRendererProps {
  fields: Record<string, any>;
  multiFilterFields?: Record<string, any> | null;
  // Page-level switches from cms_category.multi_filter_switches (e.g. only_new_arrival).
  multiFilterSwitches?: Record<string, any> | null;
  pageTitle?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MATCH_TYPE_FIELDS = ['web_colour', 'web_materials', 'web_type', 'web_standards', 'web_composition', 'web_performance'];

// Which facets a page can expose, in display order. The CMS marks each with a
// `<field>_display` flag; lockedFilterFields keeps only the enabled ones.
const FILTER_FIELDS_ORDER = [
  'application', 'item_group', 'range', 'brand', 'web_colour',
  'design_style', 'web_materials', 'web_shape', 'web_size',
  'web_type', 'web_standards', 'web_composition', 'web_performance',
];

const FILTER_KEYS = [
  'item_group', 'application', 'range', 'web_colour',
  'design_style', 'brand', 'web_materials', 'web_standards',
  'web_composition', 'web_performance',
  'web_size', 'web_shape', 'web_type', 'item_attribute',
];

// ─── Field Helper ───────────────────────────────────────────────────────────────

const getFieldValue = (field: any): any => {
  if (!field) return null;
  return field.value ?? field.default ?? null;
};

// Shown when BOTH hub fields (title + description) are left blank. Keep in sync
// with the hub FilteredProductsBlockConfig placeholder.
const DEFAULT_SECTION_DESCRIPTION =
  "Our inventory features thousands of designs. Use the selection bar below to find exactly what you're looking for.";

// Mirrors the standard header's `textTransform: capitalize` for the fallback title.
const capitalizeWords = (s: string) => s.replace(/\b\w/g, (c) => c.toUpperCase());

// ─── Component ────────────────────────────────────────────────────────────────

const FilteredProductsBlockRenderer: React.FC<FilteredProductsBlockRendererProps> = ({
                                                                                       fields,
                                                                                       multiFilterFields,
                                                                                       multiFilterSwitches,
                                                                                       pageTitle,
                                                                                     }) => {
  // Stable ref so the memos below don't recompute every render.
  const mff = useMemo(() => multiFilterFields ?? {}, [multiFilterFields]);

  const itemGroup =
    (Array.isArray(mff.item_group) && mff.item_group[0]) ||
    fields?.filters?.value?.item_group?.[0] ||
    '';

  const maxWidth: string = fields?.max_width?.value ?? '';
  const hoverAnimationEnabled: boolean = getFieldValue(fields.hover_animation_enabled) ?? true;
  // Editable heading/intro from the hub. Both blank → the standard heading + intro.
  // Otherwise show exactly what was entered, so a page can have a title with no
  // description (or the reverse) — FilterComponent skips an empty one.
  const sectionTitle: string = (getFieldValue(fields.section_title) ?? '').trim();
  const sectionDescription: string = (getFieldValue(fields.section_description) ?? '').trim();
  const sectionHeader = sectionTitle || sectionDescription
    ? { title: sectionTitle, description: sectionDescription }
    : {
      title: pageTitle ? `Discover – ${capitalizeWords(pageTitle)}` : 'Discover',
      description: DEFAULT_SECTION_DESCRIPTION,
    };
  // Driven by the page-level filter switch — one switch for the whole page.
  const onlyNewArrival: boolean = multiFilterSwitches?.only_new_arrival ?? false;

  const lockedFilters = useMemo(() => {
    const result: Record<string, string[]> = {};
    for (const key of FILTER_KEYS) {
      const values = mff[key];
      if (Array.isArray(values) && values.length > 0) {
        result[key] = values;
      }
    }
    if (itemGroup && !result.item_group) {
      result.item_group = [itemGroup];
    }
    return result;
  }, [mff, itemGroup]);

  // Facets to show, from the CMS `<field>_display` flags on the category.
  const lockedFilterFields = useMemo(
    () => FILTER_FIELDS_ORDER.filter((key) => mff[`${key}_display`] === true),
    [mff]
  );

  const lockedMatchTypes = useMemo(() => {
    const result: Record<string, string> = {};
    for (const field of MATCH_TYPE_FIELDS) {
      const mt = mff[`${field}_match_type`];
      if (mt === 'and' || mt === 'or') {
        result[field] = mt;
      }
    }
    return result;
  }, [mff]);

  const initialApplications = useMemo(() => {
    const apps: string[] = Array.isArray(mff.application) && mff.application.length > 0
      ? mff.application
      : [];
    if (apps.some(a => a.toLowerCase() === 'curtaining') && !apps.includes('Dual Purpose')) {
      return [...apps, 'Dual Purpose'];
    }
    if (apps.some(a => a.toLowerCase() === 'upholstery') && !apps.includes('Dual Purpose')) {
      return [...apps, 'Dual Purpose'];
    }
    return apps;
  }, [mff]);

  // Early return after all hooks.
  // The block needs SOME scope: a locked item_group, "Only new arrivals" mode,
  // or any other locked base filter (e.g. item_attribute / Application Type — an
  // "all Outdoor products" page has no item_group). mergeLocked drops the empty
  // item_group from the request so the grid scopes by the remaining filters.
  const hasBaseScope = Object.keys(lockedFilters).length > 0;
  if (!itemGroup && !onlyNewArrival && !hasBaseScope) return null;

  return (
    <Box
      sx={{
        ...(maxWidth ? { maxWidth, mx: 'auto' } : {}),
        '& .MuiContainer-root': {
          maxWidth: '100% !important',
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
        },
        '& .MuiGrid-container': {
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
        },
      }}
    >
      <FilterComponent
        itemGroup={itemGroup}
        title={pageTitle ?? ''}
        hideHeader
        sectionHeader={sectionHeader}
        flush
        lockedFilters={lockedFilters}
        lockedFilterFields={lockedFilterFields}
        lockedMatchTypes={lockedMatchTypes}
        initialApplications={initialApplications}
        hoverAnimationEnabled={hoverAnimationEnabled}
        onlyNewArrival={onlyNewArrival}
      />
    </Box>
  );
};

export default FilteredProductsBlockRenderer;